/**
 * WorkflowStepDocuments (Step 4)
 *
 * Document upload and packet builder using TASK CARD pattern.
 * Each document requirement is a single task card with one primary action.
 * Cards are sorted: Blocked → Review → Done
 *
 * Part of Phase 8: Job Workflow Wizard
 * Updated: TurboTax UX refinements - task cards, blockers metric
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { orderBy } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FileText,
  Upload,
  Check,
  AlertCircle,
  Eye,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
  Circle,
  Sparkles,
  Download,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { dualReadJobDocuments } from '@/lib/firestore-helpers';
import { JobDocuments, type JobDocumentsHandle } from '@/components/job-detail/JobDocuments';
import { FormGenerationDialog } from '@/components/compliance/FormGenerationDialog';
import type { UseJobWorkflowReturn } from '@/hooks/useJobWorkflow';
import type { JobData } from '../WorkflowContainer';

interface WorkflowStepDocumentsProps {
  jobId: string;
  contractorId: string;
  jobData: JobData;
  workflow: UseJobWorkflowReturn;
}

// Document from Firestore
interface JobDocument {
  id: string;
  document_name: string;
  document_url: string;
  document_type: string;
  description: string;
  category: string | null;
  status: string | null;
  createdAt: string;
}

// Required document based on job/trade
interface RequiredDocument {
  category: string;
  name: string;
  description: string;
  required: boolean;
  uploadedDoc?: JobDocument;
}

// Task card for single-action UX
interface DocumentTaskCard {
  id: string;
  category: string;
  name: string;
  description: string;
  required: boolean;
  status: TaskStatus;
  uploadedDoc?: JobDocument;
  sortOrder: number;
}

// Task card status types
type TaskStatus = 'blocked' | 'review' | 'done' | 'optional_missing' | 'optional_done';

// Task card status configuration
const taskStatusConfig: Record<TaskStatus, {
  label: string;
  action: string;
  cardBorder: string;
  cardBg: string;
  iconColor: string;
  sortOrder: number;
}> = {
  blocked: {
    label: 'Required',
    action: 'Upload',
    cardBorder: 'border-red-300 dark:border-red-800',
    cardBg: 'bg-red-50 dark:bg-red-950/40',
    iconColor: 'text-red-500',
    sortOrder: 0,
  },
  review: {
    label: 'Review',
    action: 'Review',
    cardBorder: 'border-amber-300 dark:border-amber-800',
    cardBg: 'bg-amber-50 dark:bg-amber-950/40',
    iconColor: 'text-amber-500',
    sortOrder: 1,
  },
  done: {
    label: 'Done',
    action: 'View',
    cardBorder: 'border-green-300 dark:border-green-800',
    cardBg: 'bg-green-50/50 dark:bg-green-950/30',
    iconColor: 'text-green-500',
    sortOrder: 2,
  },
  optional_missing: {
    label: 'Optional',
    action: 'Upload',
    cardBorder: 'border-muted',
    cardBg: 'bg-muted/30 dark:bg-muted/20',
    iconColor: 'text-muted-foreground',
    sortOrder: 3,
  },
  optional_done: {
    label: 'Optional',
    action: 'View',
    cardBorder: 'border-green-200 dark:border-green-800',
    cardBg: 'bg-green-50/30 dark:bg-green-950/20',
    iconColor: 'text-green-400',
    sortOrder: 4,
  },
};

// Get required documents based on trade/permit type
function getRequiredDocumentsForTrade(trade: string | undefined): Array<{ category: string; name: string; description: string; required: boolean }> {
  const baseRequirements = [
    { category: 'application_form', name: 'Permit Application Form', description: 'Completed application form for your jurisdiction', required: true },
    { category: 'contractor_license', name: 'Contractor License', description: 'Copy of active contractor license', required: true },
    { category: 'insurance_coi', name: 'Certificate of Insurance', description: 'Current certificate of liability insurance', required: true },
    { category: 'owner_auth', name: 'Owner Authorization', description: 'Signed authorization from property owner', required: false },
  ];

  switch (trade) {
    case 'electrical':
      return [
        ...baseRequirements,
        { category: 'load_calculation', name: 'Load Calculation', description: 'Electrical load calculation for panel work', required: true },
        { category: 'single_line', name: 'Single Line Diagram', description: 'Single line electrical diagram', required: true },
        { category: 'riser_diagram', name: 'Riser Diagram', description: 'Electrical riser diagram (for multi-story)', required: false },
      ];
    case 'mechanical':
      return [
        ...baseRequirements,
        { category: 'load_calculation', name: 'Manual J/D/S Calculation', description: 'HVAC load calculation', required: true },
        { category: 'equipment_specs', name: 'Equipment Specifications', description: 'HVAC equipment cut sheets', required: true },
        { category: 'energy_calc', name: 'Energy Calculation', description: 'Energy compliance documentation', required: false },
      ];
    case 'plumbing':
      return [
        ...baseRequirements,
        { category: 'riser_diagram', name: 'Plumbing Riser', description: 'Plumbing riser diagram', required: true },
        { category: 'site_plan', name: 'Site Plan', description: 'Site plan showing plumbing layout', required: false },
      ];
    case 'roofing':
      return [
        ...baseRequirements,
        { category: 'product_approval', name: 'Product Approval (NOA)', description: 'Florida Product Approval for roofing materials', required: true },
        { category: 'sealed_drawings', name: 'Sealed Drawings', description: 'Engineer-sealed drawings (if required)', required: false },
      ];
    case 'building':
    default:
      return [
        ...baseRequirements,
        { category: 'building_plans', name: 'Building Plans', description: 'Architectural drawings', required: true },
        { category: 'site_plan', name: 'Site Plan', description: 'Property site plan', required: false },
        { category: 'survey', name: 'Property Survey', description: 'Recent property survey', required: false },
      ];
  }
}

export function WorkflowStepDocuments({
  jobId,
  contractorId,
  jobData,
  workflow,
}: WorkflowStepDocumentsProps) {
  const documentsRef = useRef<JobDocumentsHandle>(null);

  const [documents, setDocuments] = useState<JobDocument[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showFullDocuments, setShowFullDocuments] = useState(false);
  const [showCompletedCards, setShowCompletedCards] = useState(false);
  const [showFormGeneration, setShowFormGeneration] = useState(false);

  // Store pending upload request when JobDocuments isn't mounted yet
  const pendingUploadRef = useRef<{ category: string; requirementName: string } | null>(null);

  // Get required documents based on trade
  const requiredDocuments = getRequiredDocumentsForTrade(jobData.trade || jobData.permitType || undefined);

  // Fetch documents from Firestore
  // Only shows spinner on initial load, not during background refreshes
  const fetchDocuments = useCallback(async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setInitialLoading(true);
      }
      const snapshot = await dualReadJobDocuments(contractorId, jobId, [
        orderBy('createdAt', 'desc')
      ]);

      const data = snapshot.docs.map(doc => {
        const docData = doc.data();
        return {
          id: doc.id,
          document_name: docData.document_name || '',
          document_url: docData.document_url || '',
          document_type: docData.document_type || '',
          description: docData.description || '',
          category: docData.category || docData.complianceCategory || null,
          status: docData.status || null,
          createdAt: docData.createdAt?.toDate ? docData.createdAt.toDate().toISOString() : new Date().toISOString(),
        } as JobDocument;
      });

      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      if (isInitialLoad) {
        setInitialLoading(false);
      }
    }
  }, [contractorId, jobId]);

  useEffect(() => {
    fetchDocuments(true); // Initial load shows spinner
  }, [fetchDocuments]);

  // Build task cards from requirements and uploaded documents
  const taskCards: DocumentTaskCard[] = useMemo(() => {
    return requiredDocuments.map(req => {
      const uploadedDoc = documents.find(d => d.category === req.category);
      let status: TaskStatus;

      if (req.required) {
        if (!uploadedDoc) {
          status = 'blocked';
        } else if (uploadedDoc.status === 'verified') {
          status = 'done';
        } else {
          status = 'review'; // Uploaded but needs verification
        }
      } else {
        status = uploadedDoc ? 'optional_done' : 'optional_missing';
      }

      return {
        id: req.category,
        category: req.category,
        name: req.name,
        description: req.description,
        required: req.required,
        status,
        uploadedDoc,
        sortOrder: taskStatusConfig[status].sortOrder,
      };
    }).sort((a, b) => a.sortOrder - b.sortOrder);
  }, [requiredDocuments, documents]);

  // Calculate metrics
  const blockedCount = taskCards.filter(c => c.status === 'blocked').length;
  const reviewCount = taskCards.filter(c => c.status === 'review').length;
  const doneCount = taskCards.filter(c => c.status === 'done').length;
  const requiredTotal = taskCards.filter(c => c.required).length;

  // Progress percentage for workflow
  const progress = requiredTotal > 0
    ? Math.round((doneCount / requiredTotal) * 100)
    : 0;

  // Track last progress to prevent re-render cascades
  const lastProgressRef = useRef(progress);
  const lastSubstepCompleteRef = useRef(false);

  // Update workflow progress - only when values actually change
  useEffect(() => {
    const allRequiredDone = blockedCount === 0 && reviewCount === 0 && requiredTotal > 0;

    if (lastProgressRef.current !== progress) {
      lastProgressRef.current = progress;
      workflow.updateProgress({ packetCompletenessPct: progress });
    }

    if (allRequiredDone && !lastSubstepCompleteRef.current) {
      lastSubstepCompleteRef.current = true;
      workflow.markSubstepComplete('documents', 'requiredDocs');
      workflow.markSubstepComplete('documents', 'perDocument');
    } else if (!allRequiredDone) {
      lastSubstepCompleteRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, blockedCount, reviewCount, requiredTotal]);

  // Handle upload for specific category
  const handleUploadForCategory = (category: string, requirementName: string) => {
    if (documentsRef.current) {
      documentsRef.current.openUpload(category, requirementName);
    } else {
      // Store the pending upload request and mount JobDocuments
      pendingUploadRef.current = { category, requirementName };
      setShowFullDocuments(true);
    }
  };

  // Trigger pending upload after JobDocuments mounts
  // We need to poll because the ref isn't available immediately after showFullDocuments changes
  useEffect(() => {
    if (!showFullDocuments || !pendingUploadRef.current) return;

    const pending = pendingUploadRef.current;

    // Poll for the ref to become available
    const checkRef = setInterval(() => {
      if (documentsRef.current) {
        clearInterval(checkRef);
        const { category, requirementName } = pending;
        pendingUploadRef.current = null;
        documentsRef.current.openUpload(category, requirementName);
      }
    }, 50);

    // Cleanup if component unmounts or showFullDocuments changes
    return () => clearInterval(checkRef);
  }, [showFullDocuments]);

  // Refresh documents
  const handleRefresh = () => {
    fetchDocuments();
    if (documentsRef.current) {
      documentsRef.current.refresh();
    }
  };

  // Handle document changes from embedded component
  // Use a debounce to prevent rapid re-fetching that causes blinking
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleDocumentsChange = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    fetchTimeoutRef.current = setTimeout(() => {
      fetchDocuments();
    }, 500);
  }, [fetchDocuments]);

  // Separate cards by actionable vs completed
  const actionableCards = taskCards.filter(c => c.status === 'blocked' || c.status === 'review');
  const completedCards = taskCards.filter(c => c.status === 'done' || c.status === 'optional_done');
  const optionalMissingCards = taskCards.filter(c => c.status === 'optional_missing');

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Render a single task card
  const renderTaskCard = (card: DocumentTaskCard) => {
    const config = taskStatusConfig[card.status];
    const isBlocked = card.status === 'blocked';
    const isReview = card.status === 'review';
    const isDone = card.status === 'done' || card.status === 'optional_done';

    return (
      <div
        key={card.id}
        className={cn(
          'p-4 rounded-lg border-l-4 transition-all',
          config.cardBorder,
          config.cardBg
        )}
      >
        <div className="flex items-start gap-3">
          {/* Status Icon */}
          <div className={cn('mt-0.5', config.iconColor)}>
            {isBlocked && <AlertCircle className="h-5 w-5" />}
            {isReview && <Circle className="h-5 w-5" />}
            {isDone && <Check className="h-5 w-5" />}
            {card.status === 'optional_missing' && <Circle className="h-5 w-5 opacity-50" />}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium">{card.name}</h4>
              <Badge
                variant="outline"
                className={cn(
                  'text-xs',
                  isBlocked && 'border-red-300 text-red-700 bg-red-100 dark:border-red-800 dark:text-red-200 dark:bg-red-950/60',
                  isReview && 'border-amber-300 text-amber-700 bg-amber-100 dark:border-amber-800 dark:text-amber-200 dark:bg-amber-950/60',
                  isDone && 'border-green-300 text-green-700 bg-green-100 dark:border-green-800 dark:text-green-200 dark:bg-green-950/50',
                  card.status === 'optional_missing' && 'border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400'
                )}
              >
                {config.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {card.description}
            </p>
            {card.uploadedDoc && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <FileText className="h-3 w-3" />
                {card.uploadedDoc.document_name}
              </p>
            )}
          </div>

          {/* Primary Action(s) */}
          <div className="flex-shrink-0 flex items-center gap-2">
            {/* Form generation button for application_form card */}
            {card.category === 'application_form' && isBlocked && (
              <Button
                size="sm"
                variant="default"
                onClick={() => setShowFormGeneration(true)}
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Generate
              </Button>
            )}
            {isBlocked && (
              <Button
                size="sm"
                variant={card.category === 'application_form' ? 'outline' : 'default'}
                onClick={() => handleUploadForCategory(card.category, card.name)}
              >
                <Upload className="h-4 w-4 mr-1" />
                Upload
              </Button>
            )}
            {isReview && card.category === 'application_form' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowFormGeneration(true)}
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Regenerate
              </Button>
            )}
            {isReview && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => window.open(card.uploadedDoc?.document_url, '_blank')}
              >
                <Eye className="h-4 w-4 mr-1" />
                Review
              </Button>
            )}
            {isDone && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => window.open(card.uploadedDoc?.document_url, '_blank')}
              >
                <Eye className="h-4 w-4" />
              </Button>
            )}
            {card.status === 'optional_missing' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleUploadForCategory(card.category, card.name)}
              >
                <Upload className="h-4 w-4 mr-1" />
                Add
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Pre-Filled Form Generation Card */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary text-primary-foreground">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium">Generate Pre-Filled Permit Application</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Auto-fill your permit application with data from this job. Review, edit, and download a ready-to-submit PDF.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setShowFormGeneration(true)}
            >
              <Sparkles className="h-4 w-4 mr-1" />
              Generate
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Blockers Remaining Header - Primary Metric */}
      <div className="flex items-center justify-between">
        <div>
          {blockedCount > 0 ? (
            <h2 className="text-lg font-semibold text-red-700 dark:text-red-300">
              {blockedCount} blocker{blockedCount !== 1 ? 's' : ''} remaining
            </h2>
          ) : reviewCount > 0 ? (
            <h2 className="text-lg font-semibold text-amber-700 dark:text-amber-300">
              {reviewCount} item{reviewCount !== 1 ? 's' : ''} to review
            </h2>
          ) : (
            <h2 className="text-lg font-semibold text-green-700 dark:text-green-300">
              All documents ready
            </h2>
          )}
          <p className="text-sm text-muted-foreground">
            {doneCount} of {requiredTotal} required documents complete
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Actionable Task Cards - Always Visible */}
      {actionableCards.length > 0 && (
        <div className="space-y-3">
          {actionableCards.map(renderTaskCard)}
        </div>
      )}

      {/* Completed Cards - Collapsible */}
      {completedCards.length > 0 && (
        <Card>
          <button
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-muted/50 rounded-t-lg transition-colors"
            onClick={() => setShowCompletedCards(!showCompletedCards)}
          >
            <span className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-300">
              <Check className="h-4 w-4" />
              {completedCards.length} document{completedCards.length !== 1 ? 's' : ''} complete
            </span>
            {showCompletedCards ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          {showCompletedCards && (
            <CardContent className="pt-0 space-y-3">
              {completedCards.map(renderTaskCard)}
            </CardContent>
          )}
        </Card>
      )}

      {/* Optional Documents - Collapsible */}
      {optionalMissingCards.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Optional Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {optionalMissingCards.map(renderTaskCard)}
          </CardContent>
        </Card>
      )}

      {/* Full Document Manager Toggle */}
      <Button
        variant="outline"
        className="w-full"
        onClick={() => setShowFullDocuments(!showFullDocuments)}
      >
        <FileText className="h-4 w-4 mr-2" />
        {showFullDocuments ? 'Hide' : 'Show'} Full Document Manager
      </Button>

      {/* Embedded JobDocuments Component */}
      {showFullDocuments && (
        <JobDocuments
          ref={documentsRef}
          jobId={jobId}
          contractorId={contractorId}
          jobAddress={jobData.location || jobData.title}
          onDocumentsChange={handleDocumentsChange}
        />
      )}

      {/* Form Generation Dialog */}
      <FormGenerationDialog
        open={showFormGeneration}
        onOpenChange={setShowFormGeneration}
        jobId={jobId}
        jurisdictionCode={jobData.jurisdictionCode || undefined}
        permitType={jobData.permitType || jobData.trade || undefined}
        jurisdictionId={jobData.jurisdictionId || null}
        projectValue={jobData.estimatedValue}
      />
    </div>
  );
}
